<template>
  <div class="modal-overlay" v-if="visible" @click.self="close">
    <div class="modal-box modal-guide-box">
      <h3>{{ guideData.title || '新手引导 · 出入金流程' }}</h3>
      <p class="guide-intro">{{ guideData.desc || '平台资金采用 USDT 与 USD 双账户管理，汇率固定 1:1。请按以下步骤完成入金与出金。' }}</p>
      <div class="guide-block">
        <h4>入金流程</h4>
        <ol class="guide-steps">
          <li v-for="(s, i) in depositSteps" :key="'d'+i" class="guide-step-item">
            <strong>{{ i + 1 }}. {{ s.text }}</strong>
            <img v-if="s.image" :src="getFullImageUrl(s.image)" class="guide-step-img" alt="步骤配图">
          </li>
        </ol>
        <button type="button" class="btn-outline-web" @click="goDeposit">前往 USDT 入金</button>
      </div>
      <div class="guide-block">
        <h4>出金流程</h4>
        <ol class="guide-steps">
          <li v-for="(s, i) in withdrawSteps" :key="'w'+i" class="guide-step-item">
            <strong>{{ i + 1 }}. {{ s.text }}</strong>
            <img v-if="s.image" :src="getFullImageUrl(s.image)" class="guide-step-img" alt="步骤配图">
          </li>
        </ol>
        <button type="button" class="btn-outline-web" @click="goExchange">前往币种兑换</button>
      </div>
      <div class="guide-block guide-tips-block" v-if="guideData.tips && guideData.tips.length">
        <h4>温馨提示</h4>
        <div class="guide-tips-list">
          <div v-for="(tip, i) in guideData.tips" :key="'t'+i" class="rule-item">
            <span class="step-icon">!</span>
            <span class="rule-text">{{ tip }}</span>
          </div>
        </div>
      </div>
      <div class="modal-btns" style="margin-top:16px">
        <button class="cancel" @click="close">关闭</button>
        <button class="confirm" @click="openChat">联系客服</button>
      </div>
    </div>
  </div>
</template>

<script>
import { getPublicGuideConfig } from '@/api/content'
import { getGuideConfig as getLocalGuideConfig } from '@/config/guide'

export default {
  name: 'GuideModal',
  props: { visible: Boolean },
  data() {
    return {
      guideData: {},
      depositSteps: [],
      withdrawSteps: []
    }
  },
  watch: {
    visible(val) {
      if (val) this.fetchGuide()
    }
  },
  created() {
    // 初始化使用本地默认数据
    const local = getLocalGuideConfig()
    this.depositSteps = local.deposit.map(s => ({ text: s.title, image: '' }))
    this.withdrawSteps = local.withdraw.map(s => ({ text: s.title, image: '' }))
    this.guideData = { title: local.title, desc: local.desc, tips: local.tips || [] }
  },
  methods: {
    close() { this.$emit('close') },
    goDeposit() { this.close(); this.$router.push('/account/deposit') },
    goExchange() { this.close(); this.$router.push('/account/exchange') },
    openChat() { this.close(); this.$emit('open-chat') },
    async fetchGuide() {
      try {
        const res = await getPublicGuideConfig()
        const data = res.data || res
        // API 返回可能是数组或单对象
        const guide = Array.isArray(data) ? data[0] : data
        if (!guide) return
        this.guideData = {
          title: guide.title || '',
          desc: guide.description || '',
          tips: Array.isArray(guide.tips)
            ? guide.tips
            : guide.tips
              ? String(guide.tips).split('\n').map(s => s.trim()).filter(Boolean)
              : []
        }
        // 解析 depositSteps / withdrawSteps（JSON 字符串 → 数组）
        if (guide.depositSteps) {
          try {
            this.depositSteps = typeof guide.depositSteps === 'string'
              ? JSON.parse(guide.depositSteps)
              : guide.depositSteps
          } catch (e) { console.warn('depositSteps 解析失败', e) }
        }
        if (guide.withdrawSteps) {
          try {
            this.withdrawSteps = typeof guide.withdrawSteps === 'string'
              ? JSON.parse(guide.withdrawSteps)
              : guide.withdrawSteps
          } catch (e) { console.warn('withdrawSteps 解析失败', e) }
        }
      } catch (e) {
        console.warn('获取新手引导配置失败，使用本地默认', e)
      }
    },
    getFullImageUrl(src) {
      if (!src) return ''
      if (src.startsWith('data:')) return src
      // 完整 URL：提取 pathname 后走 /api 代理
      if (src.startsWith('http://') || src.startsWith('https://')) {
        try {
          const urlObj = new URL(src)
          const pathname = urlObj.pathname
          if (pathname.startsWith('/api/')) return pathname
          if (pathname.startsWith('/dev-api/')) return pathname.replace('/dev-api/', '/api/')
          return '/api' + (pathname.startsWith('/') ? '' : '/') + pathname
        } catch (e) {
          return src
        }
      }
      if (src.startsWith('/api/')) return src
      if (src.startsWith('/dev-api/')) return src.replace('/dev-api/', '/api/')
      // 其他相对路径 → 加 /api 前缀走代理
      return '/api' + (src.startsWith('/') ? '' : '/') + src
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-guide-box { width: 520px; max-width: 92vw; max-height: 85vh; overflow-y: auto; h3 { margin-bottom: 8px; } }
.guide-intro { font-size: 13px; color: #666; line-height: 1.6; margin-bottom: 16px; }
.guide-block { background: var(--bg); border-radius: 8px; padding: 14px; margin-bottom: 12px; h4 { font-size: 14px; margin: 0 0 8px; } }
.guide-steps { margin: 0 0 12px 18px; font-size: 13px; color: #666; line-height: 1.8; }
.guide-step-item { margin-bottom: 12px; strong { font-size: 14px; color: #333; } }
.guide-step-desc { display: block; font-size: 12px; color: #999; margin-top: 2px; }
.guide-step-img { max-width: 100%; max-height: 160px; border-radius: 6px; margin-top: 6px; object-fit: contain; }
.guide-tips-block { h4 { margin-bottom: 4px; } }
.guide-tips-list { margin-top: 4px; }
.rule-item { display: flex; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--border); font-size: 13px; color: #999; line-height: 1.6; &:last-child { border-bottom: none; padding-bottom: 0; } }
.step-icon { flex-shrink: 0; width: 22px; height: 22px; background: var(--red-soft, #fff1f0); color: var(--red, #e64340); border-radius: 50%; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
.rule-text { flex: 1; }
.btn-outline-web { padding: 8px 16px; border: 1px solid var(--red); color: var(--red); background: #fff; border-radius: 6px; font-size: 13px; cursor: pointer; font-family: inherit; }
.modal-btns { display: flex; gap: 12px; justify-content: flex-end; .cancel { padding: 8px 20px; border: 1px solid var(--border); background: #fff; border-radius: 6px; font-size: 14px; cursor: pointer; font-family: inherit; } .confirm { padding: 8px 20px; background: var(--red); color: #fff; border: none; border-radius: 6px; font-size: 14px; cursor: pointer; font-family: inherit; } }
</style>
